// src/components/Hizmetler.js
// Faz 5 - Modernize edilmiş hizmet yönetimi UI

import React, { useState, useEffect } from 'react';
import ServiceFormModal from './ServiceFormModal';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, query, orderBy, onSnapshot, doc, deleteDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { defaultServices } from '../data/defaultServices';

// MUI Bileşenleri
import {
  Box, Button, Typography, Paper, CircularProgress, Alert, AlertTitle,
  Accordion, AccordionSummary, AccordionDetails, Chip, IconButton,
  Grid, Card, CardContent, CardActions, Divider, Tooltip, Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CategoryIcon from '@mui/icons-material/Category';

// Kategori renkleri
const categoryColors = {
  'PPF Kaplama': '#e65100',
  'Cam Filmi': '#1565c0',
  'Seramik Kaplama': '#7b1fa2',
  'Detay Temizlik': '#2e7d32',
  'Yıkama': '#00838f',
  'Göçük Düzeltme': '#5d4037',
  'Diğer': '#616161'
};

const getCategoryColor = (category) => categoryColors[category] || '#616161';

const Hizmetler = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingService, setEditingService] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(false);

  useEffect(() => {
    const q = query(collection(db, "services"), orderBy("hizmetAdi", "asc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setServices(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Paketler ve tekil hizmetleri ayır
  const packages = services.filter(s => s.isPaket);
  const singleServices = services.filter(s => !s.isPaket);

  // Tekil hizmetleri kategoriye göre grupla
  const groupedServices = singleServices.reduce((acc, service) => {
    const category = service.tip || 'Diğer';
    if (!acc[category]) acc[category] = [];
    acc[category].push(service);
    return acc;
  }, {});

  const handleSaveService = async (serviceData) => {
    try {
      if (editingService) {
        await updateDoc(doc(db, 'services', editingService.id), serviceData);
        alert("Hizmet başarıyla güncellendi.");
      } else {
        await addDoc(collection(db, "services"), { ...serviceData, eklenmeTarihi: serverTimestamp() });
        alert("Hizmet başarıyla eklendi!");
      }
    } catch (e) { console.error("Hata: ", e); }
  };

  const handleDeleteService = async (id, ad) => {
    if (window.confirm(`'${ad}' hizmetini silmek istediğinizden emin misiniz?`)) {
      try { await deleteDoc(doc(db, 'services', id)); alert("Hizmet silindi."); }
      catch (e) { console.error("Silme hatası: ", e); }
    }
  };

  const handleSeedServices = async () => {
    if (window.confirm('Solar Gard varsayılan hizmet listesini eklemek istediğinizden emin misiniz?')) {
      try {
        const batch = writeBatch(db);
        const servicesRef = collection(db, "services");
        defaultServices.forEach(service => {
          const docRef = doc(servicesRef);
          batch.set(docRef, { ...service, eklenmeTarihi: serverTimestamp() });
        });
        await batch.commit();
        alert('Varsayılan hizmetler başarıyla eklendi!');
      } catch (e) {
        console.error("Hizmetler eklenirken hata oluştu: ", e);
        alert("Bir hata oluştu, hizmetler eklenemedi.");
      }
    }
  };

  const openModal = (service = null) => {
    setEditingService(service);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingService(null);
  };

  if (loading) {
    return <CircularProgress sx={{ display: 'block', margin: 'auto', my: 4 }} />;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" fontWeight="bold">Hizmet Yönetimi</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openModal()}>
          Yeni Hizmet Ekle
        </Button>
      </Box>

      {services.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            <AlertTitle>Bilgi</AlertTitle>
            Henüz kayıtlı hizmet bulunmamaktadır.
          </Alert>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AutoAwesomeIcon />}
            onClick={handleSeedServices}
          >
            Solar Gard Varsayılan Hizmetlerini Ekle
          </Button>
        </Paper>
      ) : (
        <>
          {/* PAKETLER */}
          {packages.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalOfferIcon color="primary" />
                Paketler ({packages.length})
              </Typography>
              <Grid container spacing={2}>
                {packages.map((pkg) => (
                  <Grid item xs={12} md={6} lg={4} key={pkg.id}>
                    <Card sx={{
                      height: '100%',
                      borderTop: '4px solid',
                      borderColor: 'primary.main',
                      transition: 'box-shadow 0.2s',
                      '&:hover': { boxShadow: 6 }
                    }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography variant="h6" fontWeight="bold">{pkg.hizmetAdi}</Typography>
                          <Chip
                            label={`${pkg.fiyat?.toLocaleString('tr-TR')} ₺`}
                            color="primary"
                            size="small"
                          />
                        </Box>
                        {pkg.aciklama && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {pkg.aciklama}
                          </Typography>
                        )}

                        {/* Alt Hizmetler Accordion */}
                        {pkg.paketIcerikleri?.length > 0 && (
                          <Accordion
                            sx={{
                              boxShadow: 'none',
                              border: '1px solid',
                              borderColor: 'divider',
                              '&:before': { display: 'none' }
                            }}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="body2" fontWeight="medium">
                                İçerik ({pkg.paketIcerikleri.length} hizmet)
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                              <Stack spacing={1}>
                                {pkg.paketIcerikleri.map((item, idx) => (
                                  <Box
                                    key={idx}
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      p: 1,
                                      bgcolor: 'action.hover',
                                      borderRadius: 1
                                    }}
                                  >
                                    <Box>
                                      <Typography variant="body2">{item.hizmetAdi}</Typography>
                                      <Typography variant="caption" color="text.secondary">{item.tip}</Typography>
                                    </Box>
                                  </Box>
                                ))}
                              </Stack>
                            </AccordionDetails>
                          </Accordion>
                        )}
                      </CardContent>
                      <Divider />
                      <CardActions sx={{ justifyContent: 'flex-end' }}>
                        <Tooltip title="Düzenle">
                          <IconButton size="small" color="primary" onClick={() => openModal(pkg)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Sil">
                          <IconButton size="small" color="error" onClick={() => handleDeleteService(pkg.id, pkg.hizmetAdi)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* TEKİL HİZMETLER - KATEGORİ BAZLI */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CategoryIcon color="action" />
              Tekil Hizmetler ({singleServices.length})
            </Typography>

            {Object.keys(groupedServices).sort().map((category) => (
              <Accordion
                key={category}
                expanded={expandedCategory === category}
                onChange={(e, isExpanded) => setExpandedCategory(isExpanded ? category : false)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: 'action.hover',
                    '&.Mui-expanded': { minHeight: 48 }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Chip
                      label={category}
                      size="small"
                      sx={{
                        bgcolor: getCategoryColor(category),
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {groupedServices[category].length} hizmet
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {groupedServices[category].map((service) => (
                      <Grid item xs={12} sm={6} md={4} key={service.id}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            height: '100%',
                            borderLeft: '3px solid',
                            borderLeftColor: getCategoryColor(category),
                            display: 'flex',
                            flexDirection: 'column'
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {service.hizmetAdi}
                            </Typography>
                            <Typography variant="subtitle2" color="primary" fontWeight="bold">
                              {service.fiyat?.toLocaleString('tr-TR')} ₺
                            </Typography>
                          </Box>
                          {service.garantiSuresiYil && (
                            <Chip
                              label={`${service.garantiSuresiYil} Yıl Garanti`}
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ mb: 0.5 }}
                            />
                          )}
                          {service.aciklama && (
                            <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                              {service.aciklama}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 1 }}>
                            <Tooltip title="Düzenle">
                              <IconButton size="small" onClick={() => openModal(service)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Sil">
                              <IconButton size="small" color="error" onClick={() => handleDeleteService(service.id, service.hizmetAdi)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </>
      )}

      <ServiceFormModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSave={handleSaveService}
        serviceToEdit={editingService}
      />
    </Box>
  );
};

export default Hizmetler;